import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../shared/context/AuthContext";
import { visitsAPI, stationsAPI, officeLocationsAPI } from "../../../shared/lib/api";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import { Textarea } from "../../../shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../shared/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shared/components/ui/card";
import { Checkbox } from "../../../shared/components/ui/checkbox";
import { toast } from "sonner";
import { LogIn, Building2, ArrowLeft, AlertCircle, MapPin, Plus } from "lucide-react";
import StationPicker from "../components/StationPicker";

const PURPOSE_OPTIONS = [
  { value: "breaker_maintenance", label: "Breaker Maintenance" },
  { value: "switching", label: "Switching" },
  { value: "inspection", label: "Inspection" },
  { value: "testing", label: "Testing" },
  { value: "emergency_response", label: "Emergency Response" },
  { value: "other", label: "Other" },
];

export default function StationEntryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedStation = searchParams.get("station");

  const [officeLocations, setOfficeLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Selected station from StationPicker
  const [selectedStation, setSelectedStation] = useState(null);

  // Add Station Dialog
  const [addStationOpen, setAddStationOpen] = useState(false);
  const [newStationName, setNewStationName] = useState("");
  const [newStationCode, setNewStationCode] = useState("");
  const [newStationRegion, setNewStationRegion] = useState("");
  const [newStationAddress, setNewStationAddress] = useState("");
  const [newStationCity, setNewStationCity] = useState("");
  const [newStationState, setNewStationState] = useState("");
  const [newStationZip, setNewStationZip] = useState("");
  const [newStationGpsLat, setNewStationGpsLat] = useState("");
  const [newStationGpsLng, setNewStationGpsLng] = useState("");
  const [addingStation, setAddingStation] = useState(false);

  // Add Office Location Dialog
  const [addOfficeOpen, setAddOfficeOpen] = useState(false);
  const [newOfficeName, setNewOfficeName] = useState("");
  const [newOfficeCode, setNewOfficeCode] = useState("");
  const [newOfficeAddress, setNewOfficeAddress] = useState("");
  const [addingOffice, setAddingOffice] = useState(false);

  const [officeLocationId, setOfficeLocationId] = useState("");
  const [purpose, setPurpose] = useState("");
  const [workOrderNumber, setWorkOrderNumber] = useState("");
  const [switchingOrderNumber, setSwitchingOrderNumber] = useState("");
  const [authorizationConfirmed, setAuthorizationConfirmed] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  // Load preselected station if provided
  useEffect(() => {
    if (preselectedStation) {
      stationsAPI.getById(preselectedStation).then(res => {
        if (res.data) {
          setSelectedStation(res.data);
        }
      }).catch(console.error);
    }
  }, [preselectedStation]);

  const loadData = async () => {
    try {
      const officesRes = await officeLocationsAPI.getAll({ status: "active" });
      setOfficeLocations(officesRes.data);
      
      // Auto-fill office location from user profile
      if (user?.office_location_id) {
        setOfficeLocationId(user.office_location_id);
      }
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Handle station selection from StationPicker
  const handleStationSelect = (station) => {
    setSelectedStation(station);
    
    // Auto-fill office location if station has one
    if (station?.reporting_office_id) {
      setOfficeLocationId(station.reporting_office_id);
    }
  };

  const handleAddStation = async (e) => {
    e.preventDefault();
    if (!newStationName) {
      toast.error("Station name is required");
      return;
    }

    setAddingStation(true);
    try {
      const res = await stationsAPI.create({
        station_name: newStationName,
        station_code: newStationCode || null,
        region: newStationRegion || null,
        street_address: newStationAddress || null,
        city: newStationCity || null,
        state: newStationState || null,
        zip_code: newStationZip || null,
        gps_lat: newStationGpsLat ? parseFloat(newStationGpsLat) : null,
        gps_lng: newStationGpsLng ? parseFloat(newStationGpsLng) : null,
      });
      
      toast.success("Station added successfully");
      setAddStationOpen(false);
      setNewStationName("");
      setNewStationCode("");
      setNewStationRegion("");
      setNewStationAddress("");
      setNewStationCity("");
      setNewStationState("");
      setNewStationZip("");
      setNewStationGpsLat("");
      setNewStationGpsLng("");
      
      // Select the newly created station
      setSelectedStation(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add station");
    } finally {
      setAddingStation(false);
    }
  };

  const handleAddOffice = async (e) => {
    e.preventDefault();
    if (!newOfficeName) {
      toast.error("Office name is required");
      return;
    }

    setAddingOffice(true);
    try {
      const res = await officeLocationsAPI.create({
        office_name: newOfficeName,
        office_code: newOfficeCode || null,
        address: newOfficeAddress || null,
      });
      
      toast.success("Office location added successfully");
      setAddOfficeOpen(false);
      setNewOfficeName("");
      setNewOfficeCode("");
      setNewOfficeAddress("");
      
      // Reload offices and select the new one
      const officesRes = await officeLocationsAPI.getAll({ status: "active" });
      setOfficeLocations(officesRes.data);
      setOfficeLocationId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add office location");
    } finally {
      setAddingOffice(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedStation || !purpose) {
      toast.error("Please select a station and purpose");
      return;
    }

    if (purpose === "other" && !notes) {
      toast.error("Please provide notes for 'Other' purpose");
      return;
    }

    setSubmitting(true);
    try {
      const res = await visitsAPI.entry({
        station_id: selectedStation.id,
        office_location_id: officeLocationId || null,
        purpose,
        work_order_number: workOrderNumber || null,
        switching_order_number: switchingOrderNumber || null,
        authorization_status: authorizationConfirmed ? "confirmed" : null,
        notes: notes || null,
      });

      toast.success("Station entry logged successfully");
      navigate(`/visit/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log entry");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto" data-testid="station-entry-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/")}
        className="mb-4 font-mono text-sm"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>

      <Card className="border-2 border-border rounded-none">
        <CardHeader className="border-b border-border bg-muted/20">
          <CardTitle className="flex items-center gap-3 font-heading">
            <div className="h-10 w-10 bg-[#FF6B00] flex items-center justify-center">
              <LogIn className="h-5 w-5 text-white" />
            </div>
            Station Entry
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Station Selection with Search */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                  Station <span className="text-red-500">*</span>
                </Label>
                <Dialog open={addStationOpen} onOpenChange={setAddStationOpen}>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none border-2 font-mono text-xs h-8"
                      data-testid="add-station-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Station
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none border-2 max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-heading flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-[#FF6B00]" />
                        Add New Station
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddStation} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2 col-span-2">
                          <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                            Station Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            value={newStationName}
                            onChange={(e) => setNewStationName(e.target.value)}
                            placeholder="Main Substation Alpha"
                            className="h-11 rounded-none border-2 font-mono"
                            data-testid="new-station-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                            Station Code
                          </Label>
                          <Input
                            value={newStationCode}
                            onChange={(e) => setNewStationCode(e.target.value)}
                            placeholder="SS-001"
                            className="h-11 rounded-none border-2 font-mono"
                            data-testid="new-station-code-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                            Region
                          </Label>
                          <Input
                            value={newStationRegion}
                            onChange={(e) => setNewStationRegion(e.target.value)}
                            placeholder="Downtown"
                            className="h-11 rounded-none border-2 font-mono"
                            data-testid="new-station-region-input"
                          />
                        </div>
                      </div>
                      
                      <div className="border-t border-border pt-4">
                        <p className="font-mono text-xs uppercase tracking-widest font-bold mb-3 text-muted-foreground">
                          Address Information
                        </p>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                              Street Address
                            </Label>
                            <Input
                              value={newStationAddress}
                              onChange={(e) => setNewStationAddress(e.target.value)}
                              placeholder="1234 Power Line Road"
                              className="h-11 rounded-none border-2 font-mono"
                              data-testid="new-station-address-input"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-2">
                              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                                City
                              </Label>
                              <Input
                                value={newStationCity}
                                onChange={(e) => setNewStationCity(e.target.value)}
                                placeholder="Houston"
                                className="h-11 rounded-none border-2 font-mono"
                                data-testid="new-station-city-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                                State
                              </Label>
                              <Input
                                value={newStationState}
                                onChange={(e) => setNewStationState(e.target.value)}
                                placeholder="TX"
                                className="h-11 rounded-none border-2 font-mono"
                                data-testid="new-station-state-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                                ZIP
                              </Label>
                              <Input
                                value={newStationZip}
                                onChange={(e) => setNewStationZip(e.target.value)}
                                placeholder="77001"
                                className="h-11 rounded-none border-2 font-mono"
                                data-testid="new-station-zip-input"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="border-t border-border pt-4">
                        <p className="font-mono text-xs uppercase tracking-widest font-bold mb-3 text-muted-foreground">
                          GPS Coordinates
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                              Latitude
                            </Label>
                            <Input
                              type="number"
                              step="any"
                              value={newStationGpsLat}
                              onChange={(e) => setNewStationGpsLat(e.target.value)}
                              placeholder="29.7604"
                              className="h-11 rounded-none border-2 font-mono"
                              data-testid="new-station-gps-lat-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                              Longitude
                            </Label>
                            <Input
                              type="number"
                              step="any"
                              value={newStationGpsLng}
                              onChange={(e) => setNewStationGpsLng(e.target.value)}
                              placeholder="-95.3698"
                              className="h-11 rounded-none border-2 font-mono"
                              data-testid="new-station-gps-lng-input"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        type="submit"
                        disabled={addingStation}
                        className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
                        data-testid="submit-new-station-btn"
                      >
                        {addingStation ? <span className="spinner" /> : "Add Station"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Station Picker with search */}
              <StationPicker
                value={selectedStation?.station_code}
                onChange={handleStationSelect}
              />
            </div>

            {/* Office Location (Auto-filled) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-mono text-xs uppercase tracking-widest font-bold flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  Office Location (Home Base)
                </Label>
                <Dialog open={addOfficeOpen} onOpenChange={setAddOfficeOpen}>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none border-2 font-mono text-xs h-8"
                      data-testid="add-office-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Office
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none border-2 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-heading flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-[#FF6B00]" />
                        Add New Office Location
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddOffice} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                          Office Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={newOfficeName}
                          onChange={(e) => setNewOfficeName(e.target.value)}
                          placeholder="Fisk Operations Center"
                          className="h-12 rounded-none border-2 font-mono"
                          data-testid="new-office-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                          Office Code
                        </Label>
                        <Input
                          value={newOfficeCode}
                          onChange={(e) => setNewOfficeCode(e.target.value)}
                          placeholder="FISK"
                          className="h-12 rounded-none border-2 font-mono"
                          data-testid="new-office-code-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                          Address
                        </Label>
                        <Input
                          value={newOfficeAddress}
                          onChange={(e) => setNewOfficeAddress(e.target.value)}
                          placeholder="1100 Fisk Street"
                          className="h-12 rounded-none border-2 font-mono"
                          data-testid="new-office-address-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={addingOffice}
                        className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
                        data-testid="submit-new-office-btn"
                      >
                        {addingOffice ? <span className="spinner" /> : "Add Office Location"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <Select value={officeLocationId || "none"} onValueChange={(val) => setOfficeLocationId(val === "none" ? "" : val)}>
                <SelectTrigger
                  className="h-12 rounded-none border-2 font-mono"
                  data-testid="office-location-select"
                >
                  <SelectValue placeholder="Select office..." />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="none">No Office</SelectItem>
                  {officeLocations.map((office) => (
                    <SelectItem key={office.id} value={office.id}>
                      {office.office_code && `${office.office_code} - `}
                      {office.office_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {user?.office_location_name && officeLocationId === user?.office_location_id && (
                <p className="font-mono text-xs text-emerald-600 dark:text-emerald-400">
                  Auto-filled from your profile
                </p>
              )}
            </div>

            {/* Purpose Selection */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                Purpose of Entry <span className="text-red-500">*</span>
              </Label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger
                  className="h-12 rounded-none border-2 font-mono"
                  data-testid="purpose-select"
                >
                  <SelectValue placeholder="Select purpose..." />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {PURPOSE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Work Order Number */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                Work Order Number
              </Label>
              <Input
                value={workOrderNumber}
                onChange={(e) => setWorkOrderNumber(e.target.value)}
                placeholder="WO-2024-001"
                className="h-12 rounded-none border-2 font-mono"
                data-testid="work-order-input"
              />
            </div>

            {/* Switching Order Number */}
            {(purpose === "switching" || purpose === "breaker_maintenance") && (
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                  Switching Order Number
                </Label>
                <Input
                  value={switchingOrderNumber}
                  onChange={(e) => setSwitchingOrderNumber(e.target.value)}
                  placeholder="SW-2024-001"
                  className="h-12 rounded-none border-2 font-mono"
                  data-testid="switching-order-input"
                />
              </div>
            )}

            {/* Authorization Confirmation */}
            <div className="flex items-center space-x-3 p-4 bg-muted/50 border-2 border-border">
              <Checkbox
                id="authorization"
                checked={authorizationConfirmed}
                onCheckedChange={setAuthorizationConfirmed}
                className="h-6 w-6 rounded-none"
                data-testid="authorization-checkbox"
              />
              <Label
                htmlFor="authorization"
                className="font-body text-sm cursor-pointer"
              >
                I confirm I am authorized to enter this station and perform the
                stated work
              </Label>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                Notes {purpose === "other" && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes or details..."
                className="rounded-none border-2 font-mono min-h-[100px]"
                data-testid="notes-textarea"
              />
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border-2 border-amber-500">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-amber-700 dark:text-amber-400">
                This entry will be time-stamped and logged permanently. It cannot
                be deleted and forms part of the official station log.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-14 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold text-lg"
              data-testid="submit-entry-btn"
            >
              {submitting ? (
                <span className="spinner" />
              ) : (
                <>
                  <LogIn className="h-5 w-5 mr-2" />
                  Confirm Station Entry
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
